// ABOUTME: Day calendar time-grid component for displaying child's scheduled events.
// ABOUTME: Uses react-big-calendar for the time-grid with 9am-9pm default, day navigation, and current time indicator.

import { useMemo } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import { utcToFamilyTimezoneDisplay } from '../../utils/dateUtils';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const locales = { 'en-US': enUS };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

export interface CalendarDisplayEvent {
  title: string;
  start: Date;
  end: Date;
}

interface DayCalendarProps {
  events: CalendarDisplayEvent[];
  date: Date;
  familyTimezone: string;
}

export function DayCalendar({ events, date, familyTimezone }: DayCalendarProps) {
  const theme = useTheme();
  // Calculate min/max times, expanding beyond 9am-9pm if events exist outside that range
  const { minTime, maxTime } = useMemo(() => {
    const defaultMin = new Date(date);
    defaultMin.setHours(9, 0, 0, 0);
    const defaultMax = new Date(date);
    defaultMax.setHours(21, 0, 0, 0);

    let min = defaultMin;
    let max = defaultMax;

    for (const event of events) {
      if (event.start < min) {
        const expandedMin = new Date(date);
        expandedMin.setHours(event.start.getHours(), 0, 0, 0);
        min = expandedMin;
      }
      if (event.end > max) {
        const expandedMax = new Date(date);
        expandedMax.setHours(event.end.getHours() + 1, 0, 0, 0);
        max = expandedMax;
      }
    }

    return { minTime: min, maxTime: max };
  }, [events, date]);

  return (
    <Box
      data-testid="day-calendar"
      sx={{
        height: '100%',
        minHeight: 300,
        '& .rbc-calendar': {
          fontFamily: 'inherit',
        },
        '& .rbc-time-view': {
          border: 'none',
        },
        '& .rbc-time-header': {
          display: 'none',
        },
        '& .rbc-time-content': {
          borderTop: 'none',
        },
        '& .rbc-timeslot-group': {
          minHeight: 40,
          borderBottom: '1px solid',
          borderColor: 'divider',
        },
        '& .rbc-time-slot .rbc-label': {
          fontSize: '0.7rem',
          color: 'text.secondary',
          px: 0.5,
        },
        '& .rbc-event': {
          borderRadius: `${Number(theme.shape.borderRadius) / 3}px`, // = 4px
          fontSize: '0.75rem',
          padding: '2px 4px',
          border: 'none',
        },
        '& .rbc-current-time-indicator': {
          backgroundColor: 'error.main',
          height: 2,
        },
      }}
    >
      <Calendar
        localizer={localizer}
        events={events}
        date={date}
        view={Views.DAY}
        views={[Views.DAY]}
        min={minTime}
        max={maxTime}
        toolbar={false}
        onNavigate={() => {}}
        showMultiDayTimes={false}
        step={30}
        timeslots={2}
        getNow={() => utcToFamilyTimezoneDisplay(new Date(), familyTimezone)}
      />
    </Box>
  );
}
